/**
 * Retry Manager Service
 * Centralized retry logic with exponential backoff for upload resilience
 * Sprint 6 - Phase 1 Implementation
 */

import logger from '../utils/logger';
import { logTelemetry } from './telemetry';
import { db } from '../idb/chunk-db';

export interface RetryConfig {
  delays: number[];        // Exponential backoff delays in ms
  maxAttempts: number;     // Maximum retry attempts
  enableTelemetry: boolean; // Track retry events
}

export interface RetryState {
  videoGuid: string;
  chunkNo?: number;
  attempts: number;
  lastAttemptAt: number;
  nextRetryAt: number;
  error?: string;
}

export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  delays: [0, 5000, 15000, 60000, 300000], // 0s, 5s, 15s, 60s, 5min
  maxAttempts: 10,
  enableTelemetry: true,
};

export class RetryManager {
  private config: RetryConfig;
  private retryStates: Map<string, RetryState>;
  private persistenceEnabled: boolean;

  constructor(config: Partial<RetryConfig> = {}) {
    this.config = { ...DEFAULT_RETRY_CONFIG, ...config };
    this.retryStates = new Map();
    this.persistenceEnabled = true; // Sprint 8: Enable persistence by default
  }

  /**
   * Sprint 8: Load retry states from IndexedDB
   */
  async loadPersistedStates(): Promise<void> {
    if (!this.persistenceEnabled) return;

    try {
      // Load retry states from telemetry table (using metadata field)
      const retryRecords = await db.telemetry
        .where('event')
        .equals('retry_state_persisted')
        .toArray();

      retryRecords.forEach(record => {
        const { videoGuid, chunkNo, retryState } = record.metadata as any;
        const key = chunkNo !== undefined ? `${videoGuid}:${chunkNo}` : videoGuid;
        
        if (retryState) {
          this.retryStates.set(key, retryState);
        }
      });

      logger.info(`Loaded ${retryRecords.length} persisted retry states from IndexedDB`);
    } catch (error) {
      logger.error('Failed to load persisted retry states:', error);
    }
  }

  /**
   * Sprint 8: Persist retry state to IndexedDB
   */
  private async persistState(videoGuid: string, chunkNo?: number, state?: RetryState): Promise<void> {
    if (!this.persistenceEnabled || !state) return;

    try {
      await logTelemetry('retry_state_persisted', {
        videoGuid,
        chunkNo,
        retryState: state,
      });
    } catch (error) {
      logger.error('Failed to persist retry state:', error);
    }
  }

  /**
   * Get retry delay for a given attempt number
   */
  getRetryDelay(attemptNumber: number): number {
    const index = Math.min(attemptNumber - 1, this.config.delays.length - 1);
    return this.config.delays[index];
  }

  /**
   * Calculate next retry time
   */
  calculateNextRetry(attemptNumber: number): number {
    const delay = this.getRetryDelay(attemptNumber);
    return Date.now() + delay;
  }

  /**
   * Initialize retry state for a chunk
   */
  initRetryState(videoGuid: string, chunkNo?: number): RetryState {
    const key = chunkNo !== undefined ? `${videoGuid}:${chunkNo}` : videoGuid;
    
    const state: RetryState = {
      videoGuid,
      chunkNo,
      attempts: 0,
      lastAttemptAt: Date.now(),
      nextRetryAt: Date.now(),
    };

    this.retryStates.set(key, state);
    
    if (this.config.enableTelemetry) {
      logTelemetry('retry_state_initialized', {
        videoGuid,
        chunkNo,
      });
    }

    return state;
  }

  /**
   * Record a retry attempt
   * Sprint 8: Enhanced with persistence
   */
  recordAttempt(videoGuid: string, chunkNo?: number, error?: string): RetryState {
    const key = chunkNo !== undefined ? `${videoGuid}:${chunkNo}` : videoGuid;
    let state = this.retryStates.get(key);

    if (!state) {
      state = this.initRetryState(videoGuid, chunkNo);
    }

    state.attempts++;
    state.lastAttemptAt = Date.now();
    state.nextRetryAt = this.calculateNextRetry(state.attempts);
    state.error = error;

    this.retryStates.set(key, state);

    // Sprint 8: Persist state to IndexedDB
    this.persistState(videoGuid, chunkNo, state);

    if (this.config.enableTelemetry) {
      logTelemetry('retry_attempt_recorded', {
        videoGuid,
        chunkNo,
        attempts: state.attempts,
        nextRetryDelay: state.nextRetryAt - Date.now(),
        error,
      });
    }

    logger.info(`Retry attempt ${state.attempts} for ${key}, next retry in ${state.nextRetryAt - Date.now()}ms`);

    return state;
  }

  /**
   * Check if should retry
   */
  shouldRetry(videoGuid: string, chunkNo?: number): boolean {
    const key = chunkNo !== undefined ? `${videoGuid}:${chunkNo}` : videoGuid;
    const state = this.retryStates.get(key);

    if (!state) {
      return true; // First attempt
    }

    const canRetry = state.attempts < this.config.maxAttempts;
    const timeToRetry = Date.now() >= state.nextRetryAt;

    return canRetry && timeToRetry;
  }

  /**
   * Check if max retries exceeded
   */
  isMaxRetriesExceeded(videoGuid: string, chunkNo?: number): boolean {
    const key = chunkNo !== undefined ? `${videoGuid}:${chunkNo}` : videoGuid;
    const state = this.retryStates.get(key);

    return state ? state.attempts >= this.config.maxAttempts : false;
  }

  /**
   * Get retry state
   */
  getRetryState(videoGuid: string, chunkNo?: number): RetryState | undefined {
    const key = chunkNo !== undefined ? `${videoGuid}:${chunkNo}` : videoGuid;
    return this.retryStates.get(key);
  }

  /**
   * Get time until next retry
   */
  getTimeUntilNextRetry(videoGuid: string, chunkNo?: number): number {
    const state = this.getRetryState(videoGuid, chunkNo);
    if (!state) return 0;

    const timeRemaining = state.nextRetryAt - Date.now();
    return Math.max(0, timeRemaining);
  }

  /**
   * Clear retry state
   * Sprint 8: Enhanced with persistence cleanup
   */
  async clearRetryState(videoGuid: string, chunkNo?: number): Promise<void> {
    const key = chunkNo !== undefined ? `${videoGuid}:${chunkNo}` : videoGuid;
    this.retryStates.delete(key);

    // Sprint 8: Clear persisted state from IndexedDB
    if (this.persistenceEnabled) {
      try {
        await db.telemetry
          .where('event')
          .equals('retry_state_persisted')
          .filter(record => {
            const metadata = record.metadata as any;
            return metadata.videoGuid === videoGuid && 
                   (chunkNo === undefined || metadata.chunkNo === chunkNo);
          })
          .delete();
      } catch (error) {
        logger.error('Failed to clear persisted retry state:', error);
      }
    }

    if (this.config.enableTelemetry) {
      logTelemetry('retry_state_cleared', {
        videoGuid,
        chunkNo,
      });
    }
  }

  /**
   * Clear all retry states for a video
   */
  clearVideoRetryStates(videoGuid: string): void {
    const keysToDelete: string[] = [];
    
    this.retryStates.forEach((state, key) => {
      if (state.videoGuid === videoGuid) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => this.retryStates.delete(key));

    if (this.config.enableTelemetry) {
      logTelemetry('video_retry_states_cleared', {
        videoGuid,
        count: keysToDelete.length,
      });
    }

    logger.info(`Cleared ${keysToDelete.length} retry states for ${videoGuid}`);
  }

  /**
   * Get all active retry states
   */
  getAllRetryStates(): RetryState[] {
    return Array.from(this.retryStates.values());
  }

  /**
   * Get retry statistics
   */
  getRetryStats(videoGuid: string): {
    totalAttempts: number;
    chunksWithRetries: number;
    avgAttempts: number;
  } {
    const states = Array.from(this.retryStates.values()).filter(
      s => s.videoGuid === videoGuid
    );

    const totalAttempts = states.reduce((sum, s) => sum + s.attempts, 0);
    const chunksWithRetries = states.length;
    const avgAttempts = chunksWithRetries > 0 ? totalAttempts / chunksWithRetries : 0;

    return {
      totalAttempts,
      chunksWithRetries,
      avgAttempts,
    };
  }
}

// Singleton instance
let retryManagerInstance: RetryManager | null = null;

export function getRetryManager(config?: Partial<RetryConfig>): RetryManager {
  if (!retryManagerInstance) {
    retryManagerInstance = new RetryManager(config);
  }
  return retryManagerInstance;
}

export function resetRetryManager(): void {
  retryManagerInstance = null;
}
